import { generatePath, useHistory } from 'react-router-dom';
import queryString from 'query-string';

import {
  myClothes,
  promotionPackagesBalancePaymentThanks,
  promotionPackagesPayment,
  promotionPackagesPaymentMethods,
  promotionServicesBalancePaymentThanks,
  promotionServicesPayment,
  promotionServicesPaymentMethods,
} from '@urls';
import { getFieldsSingleError } from '@utils/common';
import { notifyError } from '@utils/notifications';
import { sendPromotionPurchaseEvent } from '@utils/analytics';

import {
  usePromoteProductFromBalanceMutation,
  usePromoteProductWithPackageFromBalanceMutation,
  usePromotionPaymentQuery,
} from './gql/graphql.v3.generated';

export interface PromotionTariff {
  id: number,
  bonusPrice?: number,
}

export interface PromotionPackage {
  id: number,
  bonusPrice?: string | null,
}

const BONUS_PAY_MIN_BALANCE_AMOUNT = 2;

export const usePromotionPayment = (productId: number) => {
  const history = useHistory();

  const { data, loading } = usePromotionPaymentQuery({
    variables: {
      productId,
    },
    fetchPolicy: 'cache-first',
  });
  
  const [
    promoteProductFromBalance,
    {
      loading: promoteProductFromBalanceLoading
    },
  ] = usePromoteProductFromBalanceMutation();

  const [
    promoteProductWithPackageFromBalance,
    {
      loading: promoteProductWithPackageFromBalanceLoading,
    },
  ] = usePromoteProductWithPackageFromBalanceMutation();

  const handlePayPromotionFromCard = ({
    tariffId,
    next,
    referrer,
  }: {
    tariffId: number,
    next?: string,
    referrer?: string,
  }) => {
    const pathname = generatePath(promotionServicesPayment, {
      productId,
      promotionId: tariffId,
    });

    history.push({
      pathname,
      search: queryString.stringify({
        next,
        referrer,
      }),
    });
  };

  const handlePayPromotionPackageFromCard = ({
    packageId,
    next,
    referrer,
  }: {
    packageId: number,
    next?: string,
    referrer?: string,
  }) => {
    const pathname = generatePath(promotionPackagesPayment, {
      productId,
      packageId,
    });

    history.push({
      pathname,
      search: queryString.stringify({
        next,
        referrer,
      }),
    });
  };

  const handlePayPromotionFromBalance = async ({
    tariffId,
    next,
    referrer,
  }: {
    tariffId: number,
    next?: string,
    referrer?: string,
  }) => {
    const { data: response } = await promoteProductFromBalance({
      variables: {
        productId,
        tariffId,
      },
    });

    if (response?.promoteProductFromBalance?.errors?.length) {
      const parsedErrors = getFieldsSingleError(response?.promoteProductFromBalance?.errors);

      notifyError(parsedErrors.__all__);
    } else {
      let promotion: { promotionName: string } | undefined;
      let tariffPeriod: string | undefined;

      data?.productDetails.promotions?.forEach(
        (p) => {
          const promotionTariff = p.tariffs?.find(
            (promotionTariff) => promotionTariff.id === tariffId,
          );

          if (promotionTariff) {
            promotion = p;
            tariffPeriod = promotionTariff.period;
          }
        },
      );

      if (promotion && tariffPeriod) {
        sendPromotionPurchaseEvent(
          `${promotion.promotionName}${parseInt(tariffPeriod, 10)}`,
          referrer,
          'packages',
        );
      }

      const thanksPathname = generatePath(promotionServicesBalancePaymentThanks, {
        productId,
        promotionId: tariffId,
      });

      window.location.href = history.createHref({
        pathname: thanksPathname,
        search: queryString.stringify({
          next: next || myClothes,
        }),
      });
    }
  };

  const handlePayPromotionPackageFromBalance = async ({
    packageId,
    next,
    referrer,
  }: {
    packageId: number,
    next?: string,
    referrer?: string,
  }) => {
    const { data: response } = await promoteProductWithPackageFromBalance({
      variables: {
        productId,
        packageId,
      },
    });

    if (response?.promoteProductWithPackageFromBalance?.errors?.length) {
      const parsedErrors = getFieldsSingleError(
        response?.promoteProductWithPackageFromBalance?.errors,
      );

      notifyError(parsedErrors.__all__);
    } else {
      const pack = data?.productDetails.promoPackages?.find(
        (p) => p.id === packageId,
      );

      if (pack && pack.slug) {
        sendPromotionPurchaseEvent(
          pack.slug,
          referrer,
          'packages',
        );
      }

      const thanksPathname = generatePath(promotionPackagesBalancePaymentThanks, {
        productId,
        packageId,
      });

      window.location.href = history.createHref({
        pathname: thanksPathname,
        search: queryString.stringify({
          next: next || myClothes,
        }),
      });
    }
  };

  const handleSelectPromotion = ({
    tariff,
    next,
    referrer,
  }: {
    tariff: PromotionTariff,
    next?: string,
    referrer?: string,
  }) => {
    if (Number(tariff.bonusPrice)) {
      if (data?.viewer.balance && data?.viewer.balance >= BONUS_PAY_MIN_BALANCE_AMOUNT) {
        handlePayPromotionFromBalance({
          tariffId: tariff.id,
          next,
          referrer,
        });
      } else {
        handlePayPromotionFromCard({
          tariffId: tariff.id,
          next,
          referrer,
        });
      }
    } else {
      const paymentPageUrl = generatePath(promotionServicesPaymentMethods, {
        productId,
        promotionId: tariff.id,
      });
  
      history.push({
        pathname: paymentPageUrl,
        search: queryString.stringify({
          next,
          referrer,
        }),
      });
    }
  };

  const handleSelectPromotionPackage = ({
    promotionPackage,
    next,
    referrer,
  }: {
    promotionPackage: PromotionPackage,
    next?: string,
    referrer?: string,
  }) => {
    if (Number(promotionPackage.bonusPrice)) {
      if (data?.viewer.balance && data?.viewer.balance >= BONUS_PAY_MIN_BALANCE_AMOUNT) {
        handlePayPromotionPackageFromBalance({
          packageId: promotionPackage.id,
          next,
          referrer,
        });
      } else {
        handlePayPromotionPackageFromCard({
          packageId: promotionPackage.id,
          next,
          referrer,
        });
      }
    } else {
      const paymentPageUrl = generatePath(promotionPackagesPaymentMethods, {
        productId,
        packageId: promotionPackage.id,
      });
  
      history.push({
        pathname: paymentPageUrl,
        search: queryString.stringify({
          next,
          referrer,
        }),
      });
    }
  };

  return {
    isDisabled: loading || promoteProductFromBalanceLoading || promoteProductWithPackageFromBalanceLoading,
    handleSelectPromotion,
    handleSelectPromotionPackage,
    handlePayPromotionFromCard,
    handlePayPromotionPackageFromCard,
    handlePayPromotionFromBalance,
    handlePayPromotionPackageFromBalance,
  }
};
